/**
 * Visibility Tracker
 *
 * Tracks tab visibility state to prevent refetch storms when switching browser tabs.
 *
 * Problem: Supabase realtime channels auto-reconnect when tab becomes visible,
 * triggering postgres_changes callbacks that invalidate queries and cause refetches.
 *
 * Solution: Track when tab just became visible and provide a guard function
 * that realtime callbacks can use to skip invalidation during reconnection.
 */

let isTabJustBecameVisible = false;
let visibilityDebounceTimer: ReturnType<typeof setTimeout> | null = null;

// Lock on Resume state tracking
let lastHiddenTimestamp: number | null = null;
const visibilityCallbacks: Set<(hidden: boolean) => void> = new Set();

// Initialize visibility tracking
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    const isHidden = document.hidden;

    if (isHidden) {
      // Tab just became hidden - record timestamp
      lastHiddenTimestamp = Date.now();
    } else {
      // Tab just became visible
      isTabJustBecameVisible = true;

      // Clear the flag after 5 seconds (enough for Supabase reconnection to complete)
      // Note: 2 seconds was too short on slower networks where reconnection takes longer
      if (visibilityDebounceTimer) {
        clearTimeout(visibilityDebounceTimer);
      }
      visibilityDebounceTimer = setTimeout(() => {
        isTabJustBecameVisible = false;
      }, 5000);
    }

    // Notify all subscribers
    visibilityCallbacks.forEach(callback => {
      try {
        callback(isHidden);
      } catch (error) {
        console.error('Visibility callback error:', error);
      }
    });
  });
}

/**
 * Check if we should skip query invalidation.
 * Returns true if the tab just became visible (within last 5 seconds).
 *
 * Use this in Supabase realtime subscription callbacks to prevent
 * refetch storms when switching tabs.
 *
 * @example
 * ```typescript
 * .on('postgres_changes', { ... }, (payload) => {
 *   if (shouldSkipInvalidation()) return;
 *   queryClient.invalidateQueries({ queryKey });
 * })
 * ```
 */
export function shouldSkipInvalidation(): boolean {
  return isTabJustBecameVisible;
}

/**
 * Get the timestamp when the tab was last hidden.
 * Returns null if the tab has never been hidden.
 *
 * Used by Lock on Resume feature to determine how long
 * the app has been in the background.
 */
export function getLastHiddenTimestamp(): number | null {
  return lastHiddenTimestamp;
}

/**
 * Subscribe to visibility changes.
 * Returns an unsubscribe function.
 *
 * @param callback Function called with `true` when hidden, `false` when visible
 * @returns Unsubscribe function
 *
 * @example
 * ```typescript
 * const unsubscribe = onVisibilityChange((hidden) => {
 *   if (!hidden) {
 *     // Tab just became visible - check if we need to lock
 *     const hiddenAt = getLastHiddenTimestamp();
 *     if (hiddenAt && Date.now() - hiddenAt > gracePeriodMs) {
 *       setIsLocked(true);
 *     }
 *   }
 * });
 *
 * // Cleanup
 * return () => unsubscribe();
 * ```
 */
export function onVisibilityChange(callback: (hidden: boolean) => void): () => void {
  visibilityCallbacks.add(callback);
  return () => {
    visibilityCallbacks.delete(callback);
  };
}
